import React from 'react';
import { Check, Circle, FileText, Headset, MessageCircle, Package, PackageCheck, Star, Ticket } from 'lucide-react';
import VisibilityEyeBtn from './PermVisibilityEyeBtn';
import { OVERVIEW_MAX_VISIBLE, OVERVIEW_VISIBILITY_KEYS } from './permDashVisibility';
import './PermVisaoGeralSketch.css';

const OPEN_OS_SPARK = [2, 2, 2, 2, 2, 9, 9, 9, 2, 3];
const SUP_SPARK = [9, 2, 2, 2, 2, 2, 2, 2, 9, 3];
const DONE_SPARK = [4, 5, 3, 6, 7, 6, 9];
const DELIVERED_SUP_SPARK = [2, 2, 2, 2, 2, 2, 2, 3];

function Spark({ values, lastTone }) {
  const max = Math.max(...values);
  return (
    <span className="pvg-spark" aria-hidden>
      {values.map((v, i) => (
        <span
          key={i}
          className={`pvg-spark-bar${i === values.length - 1 && lastTone ? ` pvg-spark-bar--${lastTone}` : ''}`}
          style={{ height: `${Math.max(8, (v / max) * 100)}%` }}
        />
      ))}
    </span>
  );
}

function Stacked({ parts }) {
  return (
    <span className="pvg-stack" aria-hidden>
      {parts.map((p) => (
        <span key={p.tone} className={`pvg-stack-seg pvg-tone--${p.tone}`} style={{ flexGrow: p.n }} />
      ))}
    </span>
  );
}

function Legend({ items }) {
  return (
    <span className="pvg-legend">
      {items.map((it) => (
        <span key={it.label} className="pvg-legend-item">
          <span className={`pvg-dot pvg-tone--${it.tone}`} aria-hidden />
          {it.label} <strong>{it.n}</strong>
        </span>
      ))}
    </span>
  );
}

function OvCard({ vkey, title, icon: Icon, link, visible, busy, onToggle, limitReached, children, className = '' }) {
  const blockedTip =
    !visible && limitReached ? `Máximo de ${OVERVIEW_MAX_VISIBLE} blocos — oculte outro para exibir este` : '';
  return (
    <section className={`pct-card pvg-card ${className}${visible ? '' : ' is-hidden'}`}>
      <div className="pct-card-head pvg-card-head">
        <span className="pvg-card-title">
          <span className="pvg-card-ico" aria-hidden>
            <Icon size={15} strokeWidth={1.75} />
          </span>
          {title}
        </span>
        <span className="pvg-card-tools">
          <span className="pvg-link">{link} →</span>
          <VisibilityEyeBtn
            label={title}
            visible={visible}
            disabled={busy}
            blockedTip={blockedTip}
            onClick={() => onToggle?.(vkey)}
          />
        </span>
      </div>
      <div className="pct-card-body">{children}</div>
      {!visible ? <span className="pct-not-shown-banner">Não exibido</span> : null}
    </section>
  );
}

/** Esboço estático da aba "Visão geral" (somente visualização + visibilidade por bloco). */
export default function PermVisaoGeralSketch({ visibility, onToggleVisibility, busy = false }) {
  const on = (k) => Number(visibility?.[k]) === 1;
  const shownCount = OVERVIEW_VISIBILITY_KEYS.filter(({ key }) => on(key)).length;
  const common = { busy, onToggle: onToggleVisibility, limitReached: shownCount >= OVERVIEW_MAX_VISIBLE };

  return (
    <div className="pct-dash pvg" aria-label="Esboço da Visão geral">
      <header className="pct-dash-head">
        <div className="pct-dash-head-left">
          <h2 className="pct-dash-title">Visão geral</h2>
          <p className="pct-dash-sub">
            Blocos exibidos ao cliente no portal — {shownCount} de {OVERVIEW_MAX_VISIBLE} ativos
          </p>
        </div>
      </header>

      <div className="pvg-grid">
        <OvCard vkey="openOs" title="OS em aberto" icon={Headset} link="Ver todos" visible={on('openOs')} {...common}>
          <div className="pvg-kpi-row">
            <span className="pvg-kpi">24</span>
            <span className="pvg-badge pvg-badge--red">▲ 100%</span>
            <Spark values={OPEN_OS_SPARK} lastTone="dark" />
          </div>
          <Stacked
            parts={[
              { tone: 'red', n: 10 },
              { tone: 'orange', n: 9 },
              { tone: 'grey', n: 3 },
              { tone: 'dark', n: 2 },
            ]}
          />
          <Legend
            items={[
              { tone: 'red', label: '[LCMY] ENCAMINHADO', n: 10 },
              { tone: 'orange', label: '[LCMY] FUTURO', n: 9 },
              { tone: 'grey', label: '[LCMY] PROXIMO DIA', n: 3 },
              { tone: 'dark', label: 'Outros', n: 2 },
            ]}
          />
          <div className="pvg-sec">
            <div className="pvg-sec-head">
              <span>Últimos chamados abertos</span>
              <span>3 OS</span>
            </div>
            <div className="pvg-chips">
              {['305609 · AK925', '305641 · AL505', '305662 · AL636'].map((c) => (
                <span key={c} className="pvg-chip">
                  <Ticket size={11} strokeWidth={1.75} aria-hidden />
                  {c}
                </span>
              ))}
            </div>
          </div>
        </OvCard>

        <OvCard
          vkey="openSupplies"
          title="Suprimentos em aberto"
          icon={Package}
          link="Ver todos"
          visible={on('openSupplies')}
          {...common}
        >
          <div className="pvg-kpi-row">
            <span className="pvg-kpi">115</span>
            <span className="pvg-badge">0%</span>
            <Spark values={SUP_SPARK} lastTone="dark" />
          </div>
          <Stacked
            parts={[
              { tone: 'red', n: 106 },
              { tone: 'orange', n: 9 },
            ]}
          />
          <Legend
            items={[
              { tone: 'red', label: 'Transporte', n: 106 },
              { tone: 'orange', label: 'Pedido realizado', n: 9 },
            ]}
          />
          <div className="pvg-sec">
            <div className="pvg-sec-head">
              <span>Concentração por unidade</span>
              <span>115 pedidos</span>
            </div>
            {[
              { name: 'UNIDADE DE DUQUE DE CAXIAS', n: 31 },
              { name: 'AVENIDA MARACANA', n: 4 },
              { name: 'V 135', n: 4 },
            ].map((u) => (
              <div key={u.name} className="pvg-unit-row">
                <span className="pvg-unit-name">{u.name}</span>
                <span className="pvg-unit-track">
                  <span className="pvg-unit-bar" style={{ width: `${(u.n / 31) * 100}%` }} />
                </span>
                <strong className="pvg-unit-n">{u.n}</strong>
              </div>
            ))}
          </div>
        </OvCard>

        <OvCard
          vkey="contracts"
          title="Contratos"
          icon={FileText}
          link="Gerenciar contratos"
          visible={on('contracts')}
          {...common}
        >
          <div className="pvg-tiles">
            <span className="pvg-tile pvg-tile--danger">
              <span className="pvg-tile-label">Vencidos</span>
              <span className="pvg-tile-val">
                <strong>1</strong> ação imediata
              </span>
            </span>
            <span className="pvg-tile">
              <span className="pvg-tile-label">Vencem em 60d</span>
              <span className="pvg-tile-val">
                <strong>0</strong> nenhum
              </span>
            </span>
            <span className="pvg-tile">
              <span className="pvg-tile-label">Vigentes</span>
              <span className="pvg-tile-val">
                <strong>0</strong> <em className="pvg-ok">em dia</em>
              </span>
            </span>
          </div>
          <div className="pvg-timeline" aria-hidden>
            <span className="pvg-timeline-track">
              <span className="pvg-timeline-past" />
              <span className="pvg-timeline-dot" />
              <span className="pvg-timeline-today" />
            </span>
            <span className="pvg-timeline-labels">
              <span>−18 meses</span>
              <strong>hoje</strong>
              <span>+60 dias</span>
            </span>
          </div>
          <div className="pvg-sec pvg-contract-row">
            <span>
              <code>1350/2025</code> 1350/2025
            </span>
            <span className="pvg-danger-txt">475d</span>
          </div>
        </OvCard>

        <OvCard
          vkey="satisfaction"
          title="Satisfação e atendimento"
          icon={Star}
          link="Ver todos"
          visible={on('satisfaction')}
          {...common}
        >
          <div className="pvg-sat">
            <div className="pvg-sat-score">
              <span>
                <span className="pvg-kpi">5,0</span>
                <span className="pvg-sat-of">/5</span>
              </span>
              <span className="pvg-stars" aria-hidden>
                ★★★★★
              </span>
              <span className="pvg-muted">1 avaliação · 30 dias</span>
            </div>
            <div className="pvg-sat-dist">
              {[
                { l: '5', n: 1 },
                { l: '4', n: 0 },
                { l: '3', n: 0 },
                { l: '≤2', n: 0 },
              ].map((r) => (
                <div key={r.l} className="pvg-sat-row">
                  <span>{r.l}</span>
                  <span className="pvg-unit-track">
                    <span className="pvg-unit-bar pvg-tone--orange" style={{ width: r.n ? '100%' : '0%' }} />
                  </span>
                  <span>{r.n}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pvg-box-row">
            <span className="pvg-legend-item">
              <span className="pvg-dot pvg-tone--orange" aria-hidden />
              Avaliações pendentes
            </span>
            <span>
              <strong>2</strong> <span className="pvg-muted">aguardando</span>
            </span>
          </div>
          <div className="pvg-box-row">
            <span className="pvg-legend-item">
              <span className="pvg-dot pvg-tone--grey" aria-hidden />
              Chat em aberto
            </span>
            <span className="pvg-box-right">
              <strong>0</strong>
              <span className="pvg-mini-btn">
                <MessageCircle size={11} strokeWidth={1.9} aria-hidden />
                Abrir chat
              </span>
            </span>
          </div>
        </OvCard>

        <OvCard
          vkey="completedOs"
          title="OS concluídas"
          icon={Circle}
          link="Ver todas"
          visible={on('completedOs')}
          className="pvg-card--completed"
          {...common}
        >
          <div className="pvg-kpi-row">
            <span className="pvg-kpi">34</span>
            <span className="pvg-badge pvg-badge--green">▲ 15%</span>
            <Spark values={DONE_SPARK} lastTone="green" />
            <span className="pvg-pill-ok">
              <Check size={11} strokeWidth={2.4} aria-hidden />
              95% na 1ª visita
            </span>
          </div>
          <Stacked
            parts={[
              { tone: 'orange', n: 16 },
              { tone: 'green', n: 13 },
              { tone: 'grey', n: 5 },
            ]}
          />
          <Legend
            items={[
              { tone: 'orange', label: 'Corretiva', n: 16 },
              { tone: 'green', label: 'Preventiva', n: 13 },
              { tone: 'grey', label: 'Outros', n: 5 },
            ]}
          />
          <div className="pvg-sec pvg-two-col">
            <div>
              <div className="pvg-sec-head">
                <span>Estabilidade do parque</span>
              </div>
              <p className="pvg-line">
                <strong>18</strong> de 22 sem chamados <span className="pvg-muted">(81%)</span>
              </p>
              <span className="pvg-unit-track">
                <span className="pvg-unit-bar pvg-tone--green" style={{ width: '81%' }} />
              </span>
            </div>
            <div>
              <div className="pvg-sec-head">
                <span>Maior concentração</span>
              </div>
              <p className="pvg-line">
                <strong>Duque de Caxias</strong> <span className="pvg-muted">(14 OS — 41%)</span>
              </p>
              <span className="pvg-unit-track">
                <span className="pvg-unit-bar pvg-tone--dark" style={{ width: '41%' }} />
              </span>
            </div>
          </div>
        </OvCard>

        <OvCard
          vkey="suprimentos_entregues"
          title="Suprimentos entregues"
          icon={PackageCheck}
          link="Ver todos"
          visible={on('suprimentos_entregues')}
          className="pvg-card--completed pvg-card--delivered-supplies"
          {...common}
        >
          <div className="pvg-kpi-row">
            <span className="pvg-kpi">0</span>
            <Spark values={DELIVERED_SUP_SPARK} lastTone="green" />
          </div>
          <p className="pvg-empty-hint">Nenhuma entrega no período</p>
          <div className="pvg-sec pvg-two-col">
            <div>
              <div className="pvg-sec-head">
                <span>Maior concentração de equipamento</span>
              </div>
              <p className="pvg-line pvg-line--muted">Sem entregas no período</p>
              <span className="pvg-unit-track">
                <span className="pvg-unit-bar pvg-tone--green" style={{ width: '0%' }} />
              </span>
            </div>
            <div>
              <div className="pvg-sec-head">
                <span>Maior concentração</span>
              </div>
              <p className="pvg-line pvg-line--muted">Sem entregas no período</p>
              <span className="pvg-unit-track">
                <span className="pvg-unit-bar pvg-tone--green" style={{ width: '0%' }} />
              </span>
            </div>
          </div>
        </OvCard>
      </div>
    </div>
  );
}
